import React from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TwoPagePdfViewer } from "../../components/TwoPagePdfViewer/TwoPagePdfViewer";

const UploadConfirmationModal = ({
  imageSrc,
  uploadedFileType,
  onUploadAgain,
  onNext,
  isOpen,
  closeModal,
  isValidating,
  apiCallFailed,
  decodedData,
}) => {
  const { t } = useTranslation();

  const [isFileLoaded, setIsFileLoaded] = useState(false);

  useEffect(() => {
    if (isOpen) setIsFileLoaded(false);
  }, [isOpen, imageSrc]);

  const getButtonText = () => {
    switch (true) {
      case !isFileLoaded:
        return t("verify:loading");
      case apiCallFailed:
        return t("verify:failed");
      case isValidating:
        return t("verify:validating");
      default:
        return t("verify:next");
    }
  };

  const getButtonStyle = () => {
    if (!isFileLoaded || isValidating) {
      return "cursor-not-allowed bg-gray-500";
    }
    if (apiCallFailed) {
      return "cursor-not-allowed bg-cfIssuedCredentialTemplateCardStatusRevokedBG";
    }
    if (!decodedData) {
      return "cursor-not-allowed bg-gray-500";
    }
    return "cursor-pointer bg-cfIssuedScreenTabButtonSelectedText hover:bg-cfPrimaryDark";
  };

  const isButtonDisabled =
    !isFileLoaded || apiCallFailed || isValidating || !decodedData;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 top-20 bg-black/30  backdrop-blur-sm " />
        </Transition.Child>

        <div className="fixed inset-0 top-20 overflow-y-auto px-4 md:px-0 max-h-full">
          <div className="flex items-center min-h-full justify-center text-center ">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel id="upload-confirmation-modal" className="w-full max-w-3xl  max-h-[90vh] transform overflow-x-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all overflow-y-auto">
                <button
                  id="upload-confirmation-modal-close-button"
                  type="button"
                  className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none"
                  onClick={closeModal}
                >
                  <span className="sr-only">Close</span>
                  &#10005;
                </button>

                <div className="flex items-center justify-between py-[1.313rem] px-8 font-medium w-full">
                  {uploadedFileType === "application/pdf" ? (
                    <div className="w-full px-4">
                      <TwoPagePdfViewer
                        pdfUrl={imageSrc}
                        showZoom={false}
                        heightClassName="max-h-[70vh]"
                        onRenderComplete={() => setIsFileLoaded(true)}
                      />
                    </div>
                  ) : (
                    <img
                      src={imageSrc}
                      alt=""
                      className="max-h-[60vh] w-full object-contain"
                      onLoad={() => setIsFileLoaded(true)}
                    />
                  )}
                </div>

                <div className="flex justify-end gap-5 mb-4 pr-8">
                  <button
                    id="upload-confirmation-modal-upload-again-button"
                    onClick={onUploadAgain}
                    className="border border-black text-gray-500 px-4 py-2 rounded-[8px] hover:bg-gray-50"
                    disabled={isValidating}
                  >
                    {t("verify:upload_again")}
                  </button>
                  <button
                    id="upload-confirmation-modal-next-button"
                    disabled={isButtonDisabled}
                    onClick={onNext}
                    className={`py-2.5 px-[3.40rem] text-sm font-medium text-white focus:outline-none rounded-[8px] border border-gray-200 focus:z-10 focus:ring-3 focus:ring-gray-100 dark:focus:ring-gray-500  dark:text-gray-700 dark:hover:text-gray-500 ${getButtonStyle()}`}
                  >
                    {getButtonText()}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UploadConfirmationModal;
